import { Link } from "react-router-dom";

/**
 * Overarching Website Terms of Use (looporadata.ai/terms).
 * Calculator terms live on the Value Calculator app; platform terms TBD.
 * Source: content/website-terms.md (from team legal doc, July 2026).
 */
export default function TermsPage() {
  return (
    <main className="main" id="top">
      <section className="section section--legal" aria-label="Terms of use">
        <div className="container">
          <nav className="content-page__nav" aria-label="Page">
            <Link className="link content-page__back" to="/">
              Back to home
            </Link>
          </nav>
          <article className="content-page">
            <h1 className="content-page__h1">LooporaData Website Terms of Use</h1>
            <p>
              <strong>Effective date:</strong> July 30, 2026
            </p>
            <p>
              These Website Terms of Use (these &quot;Terms&quot;) govern your access to
              and use of the LooporaData public website at looporadata.ai and its content
              (the &quot;Site&quot;), operated by LooporaData, Inc., a Delaware corporation
              (&quot;LooporaData&quot;, &quot;we&quot;, &quot;us&quot;, or &quot;our&quot;).
              By accessing or using the Site, you agree to these Terms. If you do not agree,
              do not use the Site.
            </p>

            <h2 className="content-page__h2">The Site</h2>
            <h3 className="content-page__h3">Informational purpose</h3>
            <p>
              The Site provides general information about LooporaData and its services.
              Content on the Site is for general informational purposes only and does not
              constitute financial, legal, tax, investment, regulatory, or clinical advice.
            </p>
            <h3 className="content-page__h3">Separate and supplemental terms</h3>
            <p>
              Some LooporaData products, online tools, and services we offer or may offer
              are governed by <strong>separate or supplemental terms</strong> presented when
              you access those products, tools, or services. Those supplemental terms apply{" "}
              <strong>in addition to</strong> these Terms and, for that product, tool, or
              service, control over these Terms to the extent of any conflict. These Terms
              govern your use of the public Site and general information we publish here.
            </p>
            <h3 className="content-page__h3">No relationship formed</h3>
            <p>
              Using the Site does not create an account, a commercial agreement, or any offer
              or obligation between you and LooporaData.
            </p>
            <h3 className="content-page__h3">Pre-release features</h3>
            <p>
              Some features and tools we make available may be new, pre-release, or provided
              on a trial basis. They may change, be incomplete, or be withdrawn at any time,
              and are provided on an &quot;as is&quot; and &quot;as available&quot; basis
              without any warranty.
            </p>

            <h2 className="content-page__h2">Who may use the Site</h2>
            <h3 className="content-page__h3">Eligibility</h3>
            <p>
              You must be at least 18 years old, or the age of majority where you live if
              higher, and able to form a binding contract. You will not use the Site if you
              are barred under applicable sanctions or appear on a denied-party list.
            </p>
            <h3 className="content-page__h3">Intended for United States users</h3>
            <p>
              The Site, and the features and tools we make available through it, are intended
              for users located in the United States. They are not directed to, or intended
              for use by, individuals or organizations located in the European Economic
              Area, the United Kingdom, or Switzerland, and you should not use them if you
              are located there. We make no representation that the Site or any such feature
              or tool is appropriate or available for use outside the United States.
            </p>
            <h3 className="content-page__h3">Acceptable use</h3>
            <p>You will not:</p>
            <ul className="content-page__list">
              <li>use the Site for any unlawful, fraudulent, or harmful purpose;</li>
              <li>
                probe, scan, or circumvent any security or access control, or introduce
                malicious code;
              </li>
              <li>
                scrape, overload, or interfere with the Site except through documented
                interfaces and within posted rate limits;
              </li>
              <li>misrepresent your identity, affiliation, or authority; or</li>
              <li>infringe the intellectual property or privacy rights of others.</li>
            </ul>

            <h2 className="content-page__h2">Intellectual property</h2>
            <p>
              The Site, including its software, text, graphics, branding, and other content
              (excluding third-party content), is owned by LooporaData or its licensors.
              Except for the limited right to view and use the Site under these Terms, no
              rights are granted to you. Feedback you provide may be used by LooporaData
              without restriction.
            </p>

            <h2 className="content-page__h2">Third-party links and services</h2>
            <p>
              The Site may link to or integrate third-party websites and services, which are
              governed by their own terms. LooporaData is not responsible for third-party
              content or services outside our reasonable control.
            </p>

            <h2 className="content-page__h2">Privacy</h2>
            <p>
              Our collection and use of information when you visit the Site is described in
              our{" "}
              <Link className="link" to="/privacy">
                Privacy Notice
              </Link>
              .
            </p>

            <h2 className="content-page__h2">Disclaimers</h2>
            <p>
              THE SITE IS PROVIDED &quot;AS IS&quot; AND &quot;AS AVAILABLE.&quot; TO THE
              MAXIMUM EXTENT PERMITTED BY LAW, LOOPORADATA DISCLAIMS ALL WARRANTIES, EXPRESS
              OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
              ACCURACY, AND NON-INFRINGEMENT. LOOPORADATA DOES NOT WARRANT THAT THE SITE
              WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT CONTENT WILL BE ACCURATE OR CURRENT.
            </p>

            <h2 className="content-page__h2">Limitation of liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, LOOPORADATA WILL NOT BE LIABLE FOR ANY
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR FOR ANY
              LOST PROFITS OR DATA, ARISING FROM OR RELATING TO YOUR USE OF THE SITE.
              LOOPORADATA&apos;S TOTAL AGGREGATE LIABILITY ARISING FROM OR RELATING TO THE
              SITE OR THESE TERMS WILL NOT EXCEED ONE HUNDRED U.S. DOLLARS (US$100). SOME
              JURISDICTIONS DO NOT ALLOW CERTAIN LIMITATIONS, IN WHICH CASE THOSE
              LIMITATIONS APPLY TO THE FULLEST EXTENT PERMITTED. NOTHING IN THESE TERMS
              LIMITS LIABILITY THAT CANNOT BE LIMITED UNDER APPLICABLE LAW, INCLUDING FOR
              FRAUD, GROSS NEGLIGENCE, OR WILLFUL MISCONDUCT.
            </p>

            <h2 className="content-page__h2">Indemnification</h2>
            <p>
              You will defend and indemnify LooporaData against claims arising from (a) any
              information or content you submit through the Site; (b) your breach of these
              Terms; or (c) your violation of law or third-party rights, except to the
              extent caused by LooporaData&apos;s gross negligence or willful misconduct.
              LooporaData will provide reasonable notice of any claim, and you will not
              settle any claim in a manner that imposes any obligation or admission on
              LooporaData without its prior written consent.
            </p>

            <h2 className="content-page__h2">Suspension and termination</h2>
            <p>
              We may suspend, restrict, or terminate your access to the Site, or to any
              feature or tool we make available, in whole or in part, at any time and for
              any reason, including to protect the Site or its users or to comply with law,
              with or without notice. You may stop using the Site at any time. Provisions
              that by their nature should survive termination will survive, including the
              sections on intellectual property, disclaimers, limitation of liability,
              indemnification, and the general provisions of these Terms.
            </p>

            <h2 className="content-page__h2">Changes to the Site</h2>
            <p>
              We may modify, suspend, or discontinue the Site, or any feature or tool we
              make available, or any part of it, at any time and without liability.
              LooporaData may interrupt the Site for maintenance, system updates, or other
              changes, and will inform users appropriately where practicable.
            </p>

            <h2 className="content-page__h2">General</h2>
            <h3 className="content-page__h3">Changes to these Terms</h3>
            <p>
              We may update these Terms by posting a revised version and updating the
              effective date. Continued use of the Site after the effective date constitutes
              acceptance where permitted by law.
            </p>
            <h3 className="content-page__h3">Governing law</h3>
            <p>
              These Terms are governed by the laws of the State of California, without
              regard to conflict-of-law principles.
            </p>
            <h3 className="content-page__h3">Dispute resolution</h3>
            <p>
              Before filing any legal action, the parties will make a good-faith effort to
              resolve the dispute through informal negotiation between authorized
              representatives for at least thirty (30) days following written notice. If
              unresolved after thirty days, the dispute will be brought exclusively in the
              state or federal courts located in Alameda County, California, and each party
              consents to personal jurisdiction and venue there. Either party may seek
              immediate injunctive or equitable relief in any court of competent
              jurisdiction to prevent actual or threatened misuse of the Site, intellectual
              property infringement, or a security breach.
            </p>
            <h3 className="content-page__h3">Assignment</h3>
            <p>
              LooporaData may transfer, assign, or subcontract any or all rights or
              obligations under these Terms, including in connection with a merger,
              acquisition, reorganization, or sale of assets. You may not assign or transfer
              your rights or obligations under these Terms without our prior written
              consent.
            </p>
            <h3 className="content-page__h3">Force majeure</h3>
            <p>
              The Site may be unavailable for reasons outside LooporaData&apos;s reasonable
              control, including infrastructure failures, third-party cloud provider
              outages, network failures, acts of government, and other force majeure events.
              LooporaData is not liable for non-performance caused by such events.
            </p>
            <h3 className="content-page__h3">Entire agreement; no reliance</h3>
            <p>
              These Terms, together with any separate terms you accept for a specific
              LooporaData product, tool, or service, constitute the entire agreement between
              you and LooporaData with respect to the Site and supersede all prior
              communications and agreements on that subject matter. You have not relied on
              any statement, estimate, or representation not expressly set out in these
              Terms or in such separate terms.
            </p>
            <h3 className="content-page__h3">Severability; no waiver</h3>
            <p>
              If any provision of these Terms is held invalid or unenforceable, it will be
              reformed to the minimum extent necessary to make it enforceable, and the
              remaining provisions remain in full force and effect. Our failure to enforce
              any provision is not a waiver of it.
            </p>
            <h3 className="content-page__h3">Notices</h3>
            <p>
              Notices to LooporaData must be sent to{" "}
              <a className="link" href="mailto:trust@looporadata.ai">
                trust@looporadata.ai
              </a>{" "}
              and to LooporaData, Inc., 490 43rd Street, Oakland, CA 94609. Notices to you
              may be sent to any email address you provide or posted on the Site. Notices
              are effective when sent, unless applicable law requires otherwise.
            </p>
            <h3 className="content-page__h3">Interpretation; no third-party beneficiaries</h3>
            <p>
              Headings are for convenience only and do not affect interpretation. These
              Terms do not create any third-party beneficiary rights.
            </p>
            <h3 className="content-page__h3">Contact</h3>
            <p>
              Questions about these Terms may be sent to{" "}
              <a className="link" href="mailto:trust@looporadata.ai">
                trust@looporadata.ai
              </a>
              .
            </p>
          </article>
        </div>
      </section>
    </main>
  );
}
